import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useParams, useSearchParams } from 'react-router-dom';
import { SubjectRepository } from '../../redux';
import { LoadingComponent } from '../../utils/ui';

const responseToText = (response) => {
  if (response == null) return '';
  if (typeof response === 'string') return response;

  const fields = ['data', 'result', 'content', 'text', 'value'];
  for (let index = 0; index < fields.length; index += 1) {
    const value = response[fields[index]];
    if (typeof value === 'string') return value;
  }

  return JSON.stringify(response, null, 2);
};

const safeDecodeURIComponent = value => {
  try {
    return decodeURIComponent(value || '');
  } catch (e) {
    return value || '';
  }
};

const ttlFileName = (iri) => {
  const value = safeDecodeURIComponent(iri)
    .split(/[\/#]/)
    .filter(Boolean)
    .pop();

  return `${value || 'rdf'}.ttl`;
};

const RDFDetail = ({ iri }) => {
  const [searchParams] = useSearchParams();
  const [ttl, setTtl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const downloadLinkRef = useRef(null);
  const downloadedRef = useRef('');
  const show = searchParams.has('show');
  const fileName = useMemo(() => ttlFileName(iri), [iri]);
  const ttlUrl = useMemo(() => {
    if (ttl == null) return '';

    return URL.createObjectURL(new Blob([ttl], { type: 'text/turtle;charset=utf-8' }));
  }, [ttl]);

  useEffect(() => {
    let canceled = false;

    if (!iri) {
      setTtl(null);
      setError(null);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError(null);

    Promise.resolve()
      .then(() => SubjectRepository.loadTTL(iri))
      .then(response => {
        if (!canceled) {
          setTtl(responseToText(response));
        }
      })
      .catch(e => {
        if (!canceled) {
          setTtl(null);
          setError(e || 'Cannot load TTL.');
        }
      })
      .finally(() => {
        if (!canceled) {
          setLoading(false);
        }
      });

    return () => {
      canceled = true;
    };
  }, [iri]);

  useEffect(() => {
    return () => {
      if (ttlUrl) URL.revokeObjectURL(ttlUrl);
    };
  }, [ttlUrl]);

  useEffect(() => {
    if (show || !ttlUrl || !downloadLinkRef.current || downloadedRef.current === ttlUrl) return;

    downloadedRef.current = ttlUrl;
    downloadLinkRef.current.click();
  }, [show, ttlUrl]);

  if (loading) return <LoadingComponent />;

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="Cannot load TTL"
        description={String(error)}
      />
    );
  }

  const downloadLink = (
    <a ref={downloadLinkRef} href={ttlUrl} download={fileName}>
      {show && (
        <Button icon={<DownloadOutlined />} disabled={!ttlUrl}>
          Download TTL
        </Button>
      )}
    </a>
  );

  if (!show) return downloadLink;

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        {downloadLink}
      </div>
      <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
        {ttl || ''}
      </pre>
    </div>
  );
};

export const RDFDetailRoute = () => {
  const params = useParams();
  const id = params['*'] || params.id;
  const iri = id ? safeDecodeURIComponent(id) : undefined;
  return <RDFDetail iri={iri} />;
};

export default RDFDetail;
